import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/feature/Toast';
import { useAuth } from '@/context/AuthContext';
import { formatFecha, getExpiryStatus } from '@/lib/fecha';

import ProductFormModal from '@/components/feature/ProductFormModal';
import DeleteConfirmModal from '@/components/feature/DeleteConfirmModal';

interface Producto {
  id: string;
  nombre: string;
  descripcion?: string;
  tipo_producto?: string;
  laboratorio?: string;
  principio_activo?: string;
  fecha_vencimiento?: string;
  precio_compra?: number;
  precio: number;
  stock: number;
  stock_minimo?: number;
  ubicacion?: string;
  proveedor?: string;
  numero_recibo?: string;
  imagen: string;
  created_at?: string;
  updated_at?: string;
}

interface KardexEntry {
  id: string;
  tipo: string;
  cantidad: number;
  stock_anterior: number;
  stock_nuevo: number;
  motivo: string;
  created_at: string;
  usuario_nombre?: string;
  producto_nombre?: string;
}

interface AuditoriaEntry {
  id: string;
  usuario: string;
  accion: string;
  fecha: string;
  modulo: string;
}

interface HistoryItem {
  id: string;
  title: string;
  subtitle: string;
  detail: string;
  icon: string;
  color: string;
  time: string;
}

type HistoryTab = 'hoy' | 'semana' | 'mes';

const ITEMS_PER_PAGE = 8;

export default function ProductosPage() {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [tiposList, setTiposList] = useState<string[]>([]);

  const [page, setPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Producto | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState<Producto | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [stockEditId, setStockEditId] = useState<string | null>(null);
  const [stockEditValue, setStockEditValue] = useState('');

  const [historyTab, setHistoryTab] = useState<HistoryTab>('hoy');
  const [auditoriaEntries, setAuditoriaEntries] = useState<AuditoriaEntry[]>([]);
  const [kardexEntries, setKardexEntries] = useState<KardexEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => { loadProductos(); loadHistory('hoy'); }, []);

  const loadProductos = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('productos')
        .select('*')
        .order('nombre', { ascending: true });
      if (error) throw error;
      if (data && data.length > 0) {
        setProductos(data as Producto[]);
        const tipos = [...new Set(data.map((p: Producto) => p.tipo_producto).filter(Boolean))] as string[];
        setTiposList(tipos.sort((a, b) => a.localeCompare(b)));
      } else {
        setProductos([]);
        setTiposList([]);
      }
    } catch {
      setProductos([]);
      setTiposList([]);
    } finally { setLoading(false); }
  };

  const loadHistory = async (tab: HistoryTab) => {
    setHistoryLoading(true);
    setHistoryTab(tab);
    try {
      const now = new Date();
      let desde: Date;
      switch (tab) {
        case 'hoy': desde = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0); break;
        case 'semana': {
          const day = now.getDay();
          const diff = day === 0 ? 6 : day - 1;
          desde = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff, 0, 0, 0);
          break;
        }
        case 'mes': desde = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0); break;
      }

      const { data: auditData } = await supabase
        .from('auditoria')
        .select('*')
        .eq('modulo', 'Productos')
        .gte('fecha', desde.toISOString())
        .order('fecha', { ascending: false })
        .limit(50);
      if (auditData) setAuditoriaEntries(auditData as AuditoriaEntry[]);
      else setAuditoriaEntries([]);

      const { data: kardexData } = await supabase
        .from('kardex')
        .select('*, usuarios(nombre), productos(nombre)')
        .gte('created_at', desde.toISOString())
        .order('created_at', { ascending: false })
        .limit(50);
      if (kardexData) {
        const mapped = kardexData.map((k: Record<string, unknown>) => ({
          ...k,
          usuario_nombre: (k.usuarios as { nombre: string } | null)?.nombre || 'Sistema',
          producto_nombre: (k.productos as { nombre: string } | null)?.nombre || 'Desconocido',
        }));
        setKardexEntries(mapped as KardexEntry[]);
      } else setKardexEntries([]);
    } catch { /* optional */ }
    finally { setHistoryLoading(false); }
  };

  const filtered = useMemo(() => {
    let result = [...productos];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((p) =>
        p.nombre.toLowerCase().includes(q) ||
        p.tipo_producto?.toLowerCase().includes(q) ||
        p.laboratorio?.toLowerCase().includes(q) ||
        p.principio_activo?.toLowerCase().includes(q) ||
        p.proveedor?.toLowerCase().includes(q) ||
        p.numero_recibo?.toLowerCase().includes(q)
      );
    }
    if (tipoFilter) result = result.filter((p) => p.tipo_producto === tipoFilter);
    return result;
  }, [productos, search, tipoFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  useEffect(() => { setPage(1); }, [search, tipoFilter]);

  const handleCreate = () => { setEditingProduct(null); setFormOpen(true); };
  const handleEdit = (product: Producto) => { setEditingProduct(product); setFormOpen(true); };

  const handleDeleteClick = (product: Producto) => { setDeletingProduct(product); setDeleteOpen(true); };
  const handleDeleteConfirm = async () => {
    if (!deletingProduct) return;
    setDeleteLoading(true);
    try {
      const { error } = await supabase.from('productos').delete().eq('id', deletingProduct.id);
      if (error) throw error;
      await supabase.from('auditoria').insert({
        usuario: user?.nombre || 'Sistema',
        accion: `Eliminó el producto: ${deletingProduct.nombre}`,
        modulo: 'Productos',
      });
      addToast('Producto eliminado correctamente', 'success');
      setDeleteOpen(false); setDeletingProduct(null);
      loadProductos(); loadHistory(historyTab);
    } catch (err: unknown) {
      addToast(err instanceof Error ? err.message : 'Error al eliminar', 'error');
    } finally { setDeleteLoading(false); }
  };

  const handleStockEditStart = (product: Producto) => { setStockEditId(product.id); setStockEditValue(product.stock.toString()); };
  const handleStockSave = async (productId: string) => {
    const nuevoStock = parseInt(stockEditValue, 10);
    if (Number.isNaN(nuevoStock) || nuevoStock < 0) { addToast('Ingrese un valor de stock válido', 'error'); return; }
    try {
      const oldProduct = productos.find((p) => p.id === productId);
      const oldStock = oldProduct?.stock || 0;
      const { error } = await supabase.from('productos').update({ stock: nuevoStock, updated_at: new Date().toISOString() }).eq('id', productId);
      if (error) throw error;
      const diff = nuevoStock - oldStock;
      if (diff !== 0) {
        await supabase.from('kardex').insert({
          producto_id: productId,
          tipo: diff > 0 ? 'entrada' : 'salida',
          cantidad: Math.abs(diff),
          stock_anterior: oldStock,
          stock_nuevo: nuevoStock,
          motivo: 'Ajuste manual de stock',
          usuario_id: user?.id,
        });
      }
      addToast('Stock actualizado correctamente', 'success');
      setStockEditId(null);
      loadProductos(); loadHistory(historyTab);
    } catch (err: unknown) {
      addToast(err instanceof Error ? err.message : 'Error al actualizar stock', 'error');
    }
  };

  const getStockBadge = (stock: number, stockMinimo: number = 5) => {
    if (stock <= 0) {
      return (<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-100 text-accent-700 whitespace-nowrap"><i className="ri-close-circle-line"></i>Agotado</span>);
    }
    if (stock <= stockMinimo) {
      return (<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-100 text-accent-700 whitespace-nowrap">{stock} (bajo)</span>);
    }
    return (<span className="text-sm font-semibold text-primary-600">{stock}</span>);
  };

  const getExpiryCell = (fecha?: string) => {
    const status = getExpiryStatus(fecha);
    if (status === 'none') return <span className="text-sm text-foreground-400">—</span>;
    const isAlert = status !== 'ok';
    return (
      <div className="flex flex-col gap-1 items-start">
        <span className={`text-sm font-medium whitespace-nowrap ${isAlert ? 'text-accent-600' : 'text-foreground-600'}`}>
          {formatFecha(fecha)}
        </span>
        {status === 'expired' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent-100 text-accent-700">Vencido</span>
        )}
        {status === 'soon' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent-100 text-accent-700">Por vencer</span>
        )}
      </div>
    );
  };

  const getMarginBadge = (precioCompra: number | undefined, precioVenta: number) => {
    if (!precioCompra || precioCompra <= 0) return null;
    const margin = ((precioVenta - precioCompra) / precioCompra) * 100;
    return (<span className="text-xs text-foreground-400 ml-1">({margin.toFixed(0)}% marg.)</span>);
  };

  const historyTabLabels: Record<HistoryTab, string> = { hoy: 'Hoy', semana: 'Esta Semana', mes: 'Este Mes' };

  const allHistory = useMemo<HistoryItem[]>(() => {
    const usedKardexIds = new Set<string>();
    const items: HistoryItem[] = [];

    const extractProductName = (action: string): string | null => {
      const match = action.match(/^(?:Editó|Creó|Eliminó) el producto: (.+)$/);
      return match ? match[1].trim() : null;
    };

    const formatDateTime = (iso: string) =>
      new Date(iso).toLocaleString('es-ES', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });

    auditoriaEntries.forEach((audit) => {
      const auditTime = new Date(audit.fecha).getTime();
      const productName = extractProductName(audit.accion);

      let matchedKardex: KardexEntry | undefined;
      if (productName) {
        matchedKardex = kardexEntries.find((k) => {
          if (usedKardexIds.has(k.id)) return false;
          const kardexTime = new Date(k.created_at).getTime();
          const timeDiff = Math.abs(auditTime - kardexTime);
          return k.producto_nombre === productName && timeDiff <= 120000;
        });
      }

      if (matchedKardex) {
        usedKardexIds.add(matchedKardex.id);
      }

      let icon = 'ri-file-list-3-line';
      let color = 'bg-secondary-100 text-secondary-700';
      if (audit.accion.includes('Creó') || audit.accion.includes('creó')) {
        icon = 'ri-add-line';
        color = 'bg-primary-100 text-primary-700';
      } else if (audit.accion.includes('Eliminó') || audit.accion.includes('eliminó')) {
        icon = 'ri-delete-bin-line';
        color = 'bg-accent-100 text-accent-700';
      } else if (audit.accion.includes('Editó') || audit.accion.includes('editó')) {
        icon = 'ri-edit-line';
        color = 'bg-amber-100 text-amber-700';
      }

      let subtitle = `por ${audit.usuario}`;
      if (matchedKardex) {
        const diff = matchedKardex.stock_nuevo - matchedKardex.stock_anterior;
        const diffSign = diff > 0 ? '+' : '';
        subtitle = `Stock: ${matchedKardex.stock_anterior} → ${matchedKardex.stock_nuevo}  |  ${diffSign}${diff} u.  |  ${audit.usuario}`;
      }

      items.push({
        id: audit.id,
        title: audit.accion,
        subtitle,
        detail: formatDateTime(audit.fecha),
        icon,
        color,
        time: audit.fecha,
      });
    });

    kardexEntries.forEach((k) => {
      if (usedKardexIds.has(k.id)) return;

      const diff = k.stock_nuevo - k.stock_anterior;
      const diffSign = diff > 0 ? '+' : '';

      items.push({
        id: k.id,
        title: `${k.motivo || 'Ajuste'}: ${k.producto_nombre || 'Producto'}`,
        subtitle: `Stock: ${k.stock_anterior} → ${k.stock_nuevo}  |  ${diffSign}${diff} u.  |  ${k.usuario_nombre || 'Sistema'}`,
        detail: formatDateTime(k.created_at),
        icon: k.tipo === 'entrada' ? 'ri-arrow-up-line' : k.tipo === 'salida' ? 'ri-arrow-down-line' : 'ri-swap-line',
        color: k.tipo === 'entrada' ? 'bg-primary-100 text-primary-700' : k.tipo === 'salida' ? 'bg-accent-100 text-accent-700' : 'bg-amber-100 text-amber-700',
        time: k.created_at,
      });
    });

    items.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
    return items;
  }, [auditoriaEntries, kardexEntries]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-800">Productos</h1>
          <p className="text-foreground-500 text-sm mt-1">
            {productos.length} producto{productos.length !== 1 ? 's' : ''} registrado{productos.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button onClick={handleCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i><span>Nuevo Producto</span>
        </button>
      </div>

      <div className="bg-white rounded-xl border border-background-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, tipo, laboratorio, principio activo, proveedor o recibo..."
              className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all" />
          </div>
          <select value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)}
            className="px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-700 text-sm focus:outline-none focus:border-primary-400 cursor-pointer">
            <option value="">Todos los tipos</option>
            {tiposList.map((tipo) => (<option key={tipo} value={tipo}>{tipo}</option>))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
              <p className="text-foreground-500 text-sm">Cargando productos...</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-background-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-medicine-bottle-line text-3xl text-foreground-300"></i>
            </div>
            <h3 className="text-lg font-semibold text-foreground-600 mb-1">Sin productos</h3>
            <p className="text-foreground-400 text-sm">{search || tipoFilter ? 'No se encontraron productos con los filtros aplicados' : 'Agrega el primer producto para comenzar'}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1400px]">
                <thead>
                  <tr className="border-b border-background-200 bg-background-50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Producto</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Tipo</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Laboratorio</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Principio activo</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Vencimiento</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">P. Compra</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">P. Venta</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Stock</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Stock mín.</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Ubicación</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Proveedor</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Nº recibo</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((product) => (
                    <tr key={product.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100 border border-background-200">
                            {product.imagen ? (<img src={product.imagen} alt={product.nombre} className="w-full h-full object-cover object-top" />) : (
                              <div className="w-full h-full flex items-center justify-center text-foreground-300"><i className="ri-medicine-bottle-line"></i></div>
                            )}
                          </div>
                          <p className="text-sm font-medium text-foreground-800 whitespace-nowrap max-w-[180px] truncate">{product.nombre}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {product.tipo_producto ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap">{product.tipo_producto}</span>
                        ) : (<span className="text-sm text-foreground-400">—</span>)}
                      </td>
                      <td className="px-4 py-3"><span className="text-sm text-foreground-700 whitespace-nowrap">{product.laboratorio || '—'}</span></td>
                      <td className="px-4 py-3"><span className="text-sm text-foreground-700 whitespace-nowrap">{product.principio_activo || '—'}</span></td>
                      <td className="px-4 py-3">{getExpiryCell(product.fecha_vencimiento)}</td>
                      <td className="px-4 py-3"><span className="text-sm text-foreground-600 whitespace-nowrap">Bs. {((product.precio_compra) || 0).toFixed(2)}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 whitespace-nowrap">
                          <span className="text-sm font-semibold text-foreground-700">Bs. {product.precio.toFixed(2)}</span>
                          {getMarginBadge(product.precio_compra, product.precio)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {stockEditId === product.id ? (
                          <div className="flex items-center gap-1">
                            <input type="number" min="0" value={stockEditValue} onChange={(e) => setStockEditValue(e.target.value)}
                              className="w-16 px-2 py-1 bg-background-50 border border-primary-300 rounded text-sm text-foreground-800 focus:outline-none focus:ring-1 focus:ring-primary-300" autoFocus
                              onKeyDown={(e) => { if (e.key === 'Enter') handleStockSave(product.id); if (e.key === 'Escape') setStockEditId(null); }} />
                            <button onClick={() => handleStockSave(product.id)} className="p-1 text-primary-500 hover:text-primary-600 cursor-pointer"><i className="ri-check-line"></i></button>
                            <button onClick={() => setStockEditId(null)} className="p-1 text-foreground-400 hover:text-foreground-600 cursor-pointer"><i className="ri-close-line"></i></button>
                          </div>
                        ) : (
                          <button onClick={() => handleStockEditStart(product)} className="cursor-pointer" title="Editar stock">{getStockBadge(product.stock, product.stock_minimo || 5)}</button>
                        )}
                      </td>
                      <td className="px-4 py-3"><span className="text-sm text-foreground-600">{product.stock_minimo ?? 0}</span></td>
                      <td className="px-4 py-3"><span className="text-sm text-foreground-500 whitespace-nowrap">{product.ubicacion || '—'}</span></td>
                      <td className="px-4 py-3"><span className="text-sm text-foreground-500 whitespace-nowrap">{product.proveedor || '—'}</span></td>
                      <td className="px-4 py-3"><span className="text-sm text-foreground-500 whitespace-nowrap font-mono">{product.numero_recibo || '—'}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleEdit(product)} className="p-2 rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer" title="Editar producto">
                            <i className="ri-edit-line text-lg"></i></button>
                          <button onClick={() => handleDeleteClick(product)} className="p-2 rounded-lg text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer" title="Eliminar producto">
                            <i className="ri-delete-bin-line text-lg"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-background-200 bg-background-50">
                <span className="text-xs text-foreground-500">
                  Página {safePage} de {totalPages} ({filtered.length} resultados)
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}
                    className="p-2 rounded-lg text-foreground-500 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer">
                    <i className="ri-arrow-left-s-line"></i></button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) { pageNum = i + 1; }
                    else if (safePage <= 3) { pageNum = i + 1; }
                    else if (safePage >= totalPages - 2) { pageNum = totalPages - 4 + i; }
                    else { pageNum = safePage - 2 + i; }
                    return (
                      <button key={pageNum} onClick={() => setPage(pageNum)}
                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${pageNum === safePage ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-200'}`}>
                        {pageNum}
                      </button>
                    );
                  })}
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                    className="p-2 rounded-lg text-foreground-500 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line"></i></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="bg-white rounded-xl border border-background-200 p-5">
        <h3 className="font-heading text-base font-semibold text-foreground-800 mb-4">Historial de Productos</h3>
        <div className="flex items-center gap-2 mb-4">
          {(Object.keys(historyTabLabels) as HistoryTab[]).map((tab) => (
            <button key={tab} onClick={() => loadHistory(tab)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${historyTab === tab ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}>
              {historyTabLabels[tab]}
            </button>
          ))}
        </div>

        {historyLoading ? (
          <div className="flex items-center justify-center py-8"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : allHistory.length === 0 ? (
          <p className="text-sm text-foreground-400 text-center py-8">Sin actividad en este período</p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {allHistory.map((entry) => (
              <div key={entry.id}
                className="flex items-start gap-3 p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${entry.color}`}>
                  <i className={entry.icon}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground-700">{entry.title}</p>
                  <p className="text-xs text-foreground-400 mt-0.5">{entry.subtitle}  •  {entry.detail}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ProductFormModal open={formOpen} onClose={() => { setFormOpen(false); setEditingProduct(null); }} onSaved={() => { loadProductos(); loadHistory(historyTab); }} editingProduct={editingProduct} />
      <DeleteConfirmModal open={deleteOpen} onClose={() => { setDeleteOpen(false); setDeletingProduct(null); }} onConfirm={handleDeleteConfirm} loading={deleteLoading} title="Eliminar Producto" message={`¿Estás seguro de eliminar "${deletingProduct?.nombre}"? Esta acción no se puede deshacer.`} />
    </div>
  );
}